/**
 * Booking context, pure week arithmetic for the calendar view (plan 01).
 *
 * Weeks start **Monday** (Lebanon). Dates are `YYYY-MM-DD` calendar strings
 * (the same block-date grain the rest of Booking uses). All math is done at
 * **noon UTC** so it never crosses a day boundary regardless of host timezone,
 * the same trick `whenLabel.formatWhen` uses. No timezone import needed here;
 * "what is today in Beirut" is the caller's impure edge
 * (`scheduling.wallClockAt(new Date()).date`).
 */

function atNoonUtc(date: string): Date {
  return new Date(`${date}T12:00:00Z`);
}

function toDateString(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** 0 = Monday … 6 = Sunday (matches scheduling.weekdayOf). */
export function weekdayMondayZero(date: string): number {
  return (atNoonUtc(date).getUTCDay() + 6) % 7;
}

/** Add `n` calendar days (n may be negative) to a date string. */
export function addDays(date: string, n: number): string {
  const d = atNoonUtc(date);
  d.setUTCDate(d.getUTCDate() + n);
  return toDateString(d);
}

/** Add `n` weeks to a week-start (or any) date string. */
export function addWeeks(date: string, n: number): string {
  return addDays(date, n * 7);
}

/** The Monday (week start) of the week containing `date`. Idempotent. */
export function weekStartFor(date: string): string {
  return addDays(date, -weekdayMondayZero(date));
}

/** The seven date strings of the week beginning at `weekStart` (Mon…Sun). */
export function weekDays(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
}
